import { Button, Checkbox, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useFormik, Field, FormikProvider } from "formik";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { ReactComponent as RightArrowIcon } from "assets/right-arrow.svg";
import DotElastic from "components/DotElastic";
import { InputField } from "components/InputField";
import { AUTH_TOKEN, AUTH_USER } from "constants/constants";
import { passwordYupRule } from "constants/passwordValidation";

import styles from "./SlapshotRegisterPage.module.scss";
import {
  SLAPSHOT_HOME_URL,
  SLAPSHOT_LOGIN_URL,
  SLAPSHOT_VERIFY_EMAIL_URL,
} from "../../constants/urls";
import { register, validateInviteToken } from "../../services/LoginService";
import { getIpInfo } from "../../services/UserService";
import { getRedirectPath } from "../../utils/helper";
import { useFingerprintEventId } from "../../utils/hooks";

const SlapshotRegisterPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<string>("");
  const [sourceError, setSourceError] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  const getFingerprintEventId = useFingerprintEventId();

  const roleOptions = [
    "Creative director",
    "VFX compositor",
    "Roto artist",
    "Design/Motion Graphics artist",
    "Editor",
    "Colorist",
    "Social media/content creator",
    "Project manager",
    "Management",
  ];

  const sourceOptions = [
    "Hotspring",
    "Google Search",
    "Linkedin",
    "Facebook, Instagram, TikTok",
    "YouTube",
    "From a friend",
    "Email or Newsletter",
    "Podcast or Blog",
    "News Article",
    "Event, Conference, or Webinar",
  ];

  const validateRole = (value: string | null): string => {
    if (!value) return "";
    if (value.length > 512) {
      return "Role must not exceed 512 characters";
    }
    return "";
  };

  const validateSource = (value: string | null): string => {
    if (!value) return "";
    if (value.length > 100) {
      return "Source must not exceed 100 characters";
    }
    return "";
  };

  const [agreeToTermsAndConditions, setAgreeToTermsAndConditions] =
    useState(false);
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      inviteCode: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Name is required"),
      lastName: Yup.string().max(
        512,
        "Last name must not exceed 512 characters",
      ),
      email: Yup.string()
        .email("Invalid email")
        .max(64, "Email must not exceed 64 characters")
        .required("Email id is required"),
      password: passwordYupRule,
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), ""], "Passwords must match")
        .required("Please retype you password."),
      inviteCode: Yup.string(),
    }),
    onSubmit: async (values, { setStatus }) => {
      setIsLoading(true);
      // Always send ipinfo; the backend prefers Fingerprint when its event
      // resolves and falls back to these otherwise.
      const [eventId, ipInfo] = await Promise.all([
        getFingerprintEventId(),
        getIpInfo(),
      ]);
      register(
        values.firstName,
        values.lastName,
        values.email,
        selectedRole || "",
        values.password,
        selectedSource || "",
        values.inviteCode,
        eventId ?? undefined,
        ipInfo ?? undefined,
      )
        .then((res) => {
          if (!res.verification_required) {
            localStorage.setItem(AUTH_TOKEN, res.token);
            localStorage.setItem(AUTH_USER, JSON.stringify(res.data));
            const redirectPath = getRedirectPath(location);
            navigate(redirectPath);
          } else {
            navigate(`${SLAPSHOT_VERIFY_EMAIL_URL}${location.search}`, {
              state: { email: res.data.email, id: res.data.id },
            });
          }
        })
        .catch((err) => {
          setStatus(
            err?.detail || "Could not create your account. Please try again.",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (token && !formik.values.inviteCode) {
      validateInviteToken(token).then((res) => {
        formik.setValues((v) => {
          return { ...v, email: res.email, inviteCode: token };
        });
      });
    }
  }, [formik, location.search]);

  const isDisabled = () => {
    return (
      isLoading ||
      !!formik.errors.firstName ||
      !!formik.errors.lastName ||
      !!formik.errors.email ||
      !!formik.errors.password ||
      !!formik.errors.confirmPassword ||
      !!formik.errors.inviteCode ||
      !!roleError ||
      !!sourceError ||
      !agreeToTermsAndConditions
    );
  };

  useEffect(() => {
    document.title = "Register | Slapshot";
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = "//js.hs-scripts.com/47461564.js";
    script.async = true;
    script.defer = true;
    script.id = "hs-script-loader";
    document.body.appendChild(script);
    return () => {
      document.getElementById("hs-script-loader")?.remove();
    };
  }, []);

  return (
    <FormikProvider value={formik}>
      <form className={styles.registerContainer} onSubmit={formik.handleSubmit}>
        <div className={styles.welcomeContainer}>
          <h3>
            Register on <span>Slapshot</span>
          </h3>
          <p>Sign up to create your account</p>
        </div>
        <div className={styles.formContainer}>
          {formik.status && (
            <div className={styles.formError}>{formik.status}</div>
          )}
          <div className={styles.fieldRow}>
            <Field name="firstName" component={InputField} label="First Name" />
            <Field name="lastName" component={InputField} label="Last Name" />
          </div>
          <Field name="email" component={InputField} label="Email address" />
          <div className={styles.fieldRow}>
            <Field
              name="password"
              type="password"
              component={InputField}
              placeholder="* * * * * * * * *"
              label="Password"
            />
            <Field
              name="confirmPassword"
              type="password"
              component={InputField}
              label="Confirm Password"
            />
          </div>
          <div className={styles.roleContainer}>
            <label htmlFor="role" className={styles.label}>
              Role
            </label>
            <Autocomplete
              id="role"
              options={roleOptions}
              value={selectedRole}
              onChange={(event, newValue: string | null) => {
                setSelectedRole(newValue);
                setRoleError(validateRole(newValue));
              }}
              onInputChange={(event, newInputValue) => {
                if (newInputValue && !roleOptions.includes(newInputValue)) {
                  setSelectedRole(newInputValue);
                  setRoleError(validateRole(newInputValue));
                }
              }}
              freeSolo
              className={styles.autoComplete}
              classes={{
                paper: styles.dropdownPaper,
                option: styles.dropdownOption,
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or type your role"
                  className={styles.autoCompleteInput}
                  error={!!roleError}
                  helperText={roleError}
                />
              )}
            />
          </div>
          <div className={styles.roleContainer}>
            <label htmlFor="role" className={styles.label}>
              How did you hear about Slapshot
            </label>
            <Autocomplete
              id="source"
              options={sourceOptions}
              value={selectedSource}
              onChange={(event, newValue: string | null) => {
                setSelectedSource(newValue);
                setSourceError(validateSource(newValue));
              }}
              onInputChange={(event, newInputValue) => {
                if (newInputValue && !sourceOptions.includes(newInputValue)) {
                  setSelectedSource(newInputValue);
                  setSourceError(validateSource(newInputValue));
                }
              }}
              freeSolo
              className={styles.autoComplete}
              classes={{
                paper: styles.dropdownPaper,
                option: styles.dropdownOption,
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or type"
                  className={styles.autoCompleteInput}
                  error={!!sourceError}
                  helperText={sourceError}
                />
              )}
            />
          </div>
        </div>
        <div className={styles.actions}>
          <div className={styles.termsContainer}>
            <Checkbox
              name="termsAndConditions"
              checked={agreeToTermsAndConditions}
              onChange={() => {
                setAgreeToTermsAndConditions(!agreeToTermsAndConditions);
              }}
              className={styles.checkbox}
            />
            <p>
              I agree to Slapshot's&nbsp;
              <a
                href="https://ssai-public-assets.s3.us-east-2.amazonaws.com/admin-public-media/agreements/Slapshot+-+Terms+and+Conditions+-+Oct+5th+2024.pdf"
                target="_blank"
                rel="noreferrer"
              >
                Terms & Conditions
              </a>
            </p>
          </div>
          <Button
            type="submit"
            disabled={isDisabled()}
            className="slapshotAuthButton"
          >
            {isLoading ? (
              <div className={styles.dotElasticContainer}>
                <DotElastic color="white" />
              </div>
            ) : (
              <>
                Sign up
                <RightArrowIcon style={{ marginLeft: "12px" }} />
              </>
            )}
          </Button>
        </div>
        <div className={styles.loginContainer}>
          <p>Already have an account?</p>
          <Link
            to={`${SLAPSHOT_LOGIN_URL}${location.search}`}
            className={styles.login}
          >
            Sign in here
          </Link>
        </div>
      </form>
    </FormikProvider>
  );
};

export default SlapshotRegisterPage;
